import type * as dto from '@src/dto';

export interface SubmissionSupersessionCandidate {
  id: string;
  status: dto.ReleaseStatus;
  submissionStatus: dto.SubmissionStatus;
  created: string;
  submittedAt?: string | null;
  reviewStartedAt?: string | null;
  reviewCompletedAt?: string | null;
  hubspotTicketId?: string | null;
  isLive?: boolean;
}

const SUPERSEDABLE_SUBMISSION_STATUSES = new Set<dto.SubmissionStatus>([
  'Submitted',
  'UnderReview',
  'AlphaTest',
  'Approved',
]);

const toTimestamp = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const hasSubmissionHistory = (candidate: SubmissionSupersessionCandidate) =>
  candidate.submissionStatus !== 'NotSubmitted' ||
  Boolean(candidate.hubspotTicketId) ||
  candidate.submittedAt != null ||
  candidate.reviewStartedAt != null ||
  candidate.reviewCompletedAt != null;

const hasSupersedableSubmissionState = (
  candidate: SubmissionSupersessionCandidate,
) => SUPERSEDABLE_SUBMISSION_STATUSES.has(candidate.submissionStatus);

const getSubmissionTimelineTimestamp = (
  candidate: SubmissionSupersessionCandidate,
) =>
  toTimestamp(candidate.submittedAt) ??
  toTimestamp(candidate.reviewStartedAt) ??
  toTimestamp(candidate.reviewCompletedAt) ??
  toTimestamp(candidate.created) ??
  Number.MIN_SAFE_INTEGER;

export const selectLatestSubmissionId = (
  candidates: SubmissionSupersessionCandidate[],
) => {
  let latestCandidate: SubmissionSupersessionCandidate | null = null;

  for (const candidate of candidates) {
    if (!hasSubmissionHistory(candidate)) {
      continue;
    }

    if (!latestCandidate) {
      latestCandidate = candidate;
      continue;
    }

    const candidateTimestamp = getSubmissionTimelineTimestamp(candidate);
    const latestTimestamp = getSubmissionTimelineTimestamp(latestCandidate);

    if (candidateTimestamp !== latestTimestamp) {
      if (candidateTimestamp > latestTimestamp) {
        latestCandidate = candidate;
      }
      continue;
    }

    const candidateCreatedAt =
      toTimestamp(candidate.created) ?? Number.MIN_SAFE_INTEGER;
    const latestCreatedAt =
      toTimestamp(latestCandidate.created) ?? Number.MIN_SAFE_INTEGER;

    if (candidateCreatedAt !== latestCreatedAt) {
      if (candidateCreatedAt > latestCreatedAt) {
        latestCandidate = candidate;
      }
      continue;
    }

    if (candidate.id.localeCompare(latestCandidate.id) > 0) {
      latestCandidate = candidate;
    }
  }

  return latestCandidate?.id ?? null;
};

export const getSupersededSubmissionIds = (
  candidates: SubmissionSupersessionCandidate[],
) => {
  const latestSubmissionId = selectLatestSubmissionId(candidates);

  if (!latestSubmissionId) {
    return new Set<string>();
  }

  return new Set(
    candidates
      .filter((candidate) => candidate.id !== latestSubmissionId)
      .filter((candidate) => candidate.isLive !== true)
      .filter(hasSupersedableSubmissionState)
      .map((candidate) => candidate.id),
  );
};
